import React from 'react';
import { Input, Select, DatePicker, Button, Row, Col, Space } from 'antd';
import {
  SearchOutlined,
  FilterOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import { LogFilters, Department, UserType } from './ActivityLog.types';

const { Option } = Select;
const { RangePicker } = DatePicker;

interface LogFiltersBarProps {
  filters: LogFilters;
  onChange: (filters: LogFilters) => void;
}

const DEPARTMENTS: Department[] = [
  'LabRequest',
  'ProductRegistration',
  'Import',
  'Export',
  'NominalPayment',
  'Invoice',
];

const USER_TYPES: { value: UserType; label: string }[] = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'SUPERADMIN', label: 'Super Admin' },
  { value: 'CUSTOMER', label: 'Customer' },
  { value: 'CASHIER', label: 'Cashier' },
  { value: 'LAB_TECHNICIAN', label: 'Lab Technician' },
];

const LogFiltersBar: React.FC<LogFiltersBarProps> = ({ filters, onChange }) => {
  const update = (partial: Partial<LogFilters>) => onChange({ ...filters, ...partial });

  const hasActiveFilters =
    filters.department || filters.userType || filters.status || filters.dateFrom || filters.dateTo || filters.search;

  return (
    <div
      style={{
        background: '#fafafa',
        border: '1px solid #f0f0f0',
        borderRadius: 12,
        padding: '16px 20px',
        marginBottom: 20,
      }}
    >
      <Row gutter={[12, 12]} align="middle">
        {/* Search */}
        <Col flex="1 1 220px">
          <Input
            placeholder="Search by user, action, entity…"
            prefix={<SearchOutlined style={{ color: '#bbb' }} />}
            value={filters.search || ''}
            onChange={(e) => update({ search: e.target.value })}
            allowClear
            style={{ borderRadius: 8 }}
          />
        </Col>

        {/* Department */}
        <Col flex="1 1 160px">
          <Select
            placeholder="Department"
            value={filters.department || undefined}
            onChange={(v) => update({ department: v || '' })}
            style={{ width: '100%', borderRadius: 8 }}
            allowClear
            suffixIcon={<FilterOutlined style={{ color: '#bbb' }} />}
          >
            {DEPARTMENTS.map((d) => (
              <Option key={d} value={d}>
                {d}
              </Option>
            ))}
          </Select>
        </Col>

        {/* User Type */}
        <Col flex="1 1 140px">
          <Select
            placeholder="User type"
            value={filters.userType || undefined}
            onChange={(v) => update({ userType: v || '' })}
            style={{ width: '100%' }}
            allowClear
          >
            {USER_TYPES.map((u) => (
              <Option key={u.value} value={u.value}>
                {u.label}
              </Option>
            ))}
          </Select>
        </Col>

        {/* Status */}
        <Col flex="1 1 120px">
          <Select
            placeholder="Status"
            value={filters.status || undefined}
            onChange={(v) => update({ status: v || '' })}
            style={{ width: '100%' }}
            allowClear
          >
            <Option value="success">Success</Option>
            <Option value="info">Info</Option>
            <Option value="warning">Warning</Option>
            <Option value="error">Error</Option>
          </Select>
        </Col>

        {/* Date range */}
        <Col flex="1 1 220px">
          <RangePicker
            style={{ width: '100%' }}
            onChange={(_, dateStrings) => update({ dateFrom: dateStrings[0] || '', dateTo: dateStrings[1] || '' })}
            format="YYYY-MM-DD"
          />
        </Col>

        {/* Sort */}
        <Col flex="0 0 auto">
          <Space.Compact>
            <Select
              value={filters.sortBy || 'timestamp'}
              onChange={(v) => update({ sortBy: v })}
              style={{ width: 130 }}
            >
              <Option value="timestamp">Date/Time</Option>
              <Option value="department">Department</Option>
              <Option value="userType">User Type</Option>
            </Select>
            <Button
              icon={filters.sortOrder === 'asc' ? <SortAscendingOutlined /> : <SortDescendingOutlined />}
              onClick={() => update({ sortOrder: filters.sortOrder === 'asc' ? 'desc' : 'asc' })}
              title={filters.sortOrder === 'asc' ? 'Ascending' : 'Descending'}
            />
          </Space.Compact>
        </Col>

        {/* Clear */}
        {hasActiveFilters && (
          <Col flex="0 0 auto">
            <Button
              icon={<ClearOutlined />}
              onClick={() => onChange({ sortBy: 'timestamp', sortOrder: 'desc' })}
              type="text"
              style={{ color: '#999' }}
            >
              Clear
            </Button>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default LogFiltersBar;
